import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, FileText } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api, formatApiError, fmtDate } from "@/lib/api";
import { PageHead } from "@/components/admin/PageHead";
import StatusBadge from "@/components/common/StatusBadge";
import DocPreviewDialog from "@/components/kyc/DocPreviewDialog";

const FILTERS = ["pending", "approved", "rejected", "all"];

function KycCard({ c, onPreview, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const d = c.kyc_details || {};

  const act = async (action) => {
    if (action === "reject" && note.trim().length < 3) return toast.error("Add a short reason for the client");
    setBusy(true);
    try {
      await api.post(`/admin/kyc/${c.id}/${action}`, action === "reject" ? { note } : undefined);
      toast.success(`${c.name} ${action === "approve" ? "approved" : "rejected"}`);
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="panel p-6" data-testid={`admin-kyc-card-${c.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3"><p className="font-display text-lg text-white">{c.name}</p><StatusBadge status={c.kyc_status} /></div>
          <p className="text-sm text-slate-500">{c.email} · email {c.email_verified ? "verified" : "not verified"} · submitted {fmtDate(c.kyc_submitted_at)}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {c.documents.map((doc) => (
            <button key={doc.id} onClick={() => onPreview(doc)} className="btn-ghost !px-3 !py-1.5 text-xs" data-testid={`admin-kyc-view-${doc.doc_type}-${c.id}`}>
              <FileText className="h-3.5 w-3.5" /> {doc.doc_type === "id" ? "Photo ID" : "Proof of address"}
            </button>
          ))}
        </div>
      </div>
      <dl className="mt-5 grid gap-4 text-sm sm:grid-cols-4">
        {[["Legal name", d.legal_name], ["Date of birth", d.date_of_birth], ["Country", d.country], ["Address", d.address]].map(([k, v]) => (
          <div key={k}><dt className="eyebrow">{k}</dt><dd className="mt-1 text-slate-200">{v || "—"}</dd></div>
        ))}
      </dl>
      {c.kyc_note && <p className="mt-4 text-sm text-red-400">Note: {c.kyc_note}</p>}
      {c.kyc_status === "pending" && (
        <div className="mt-6 flex flex-col gap-3 border-t border-line pt-5 sm:flex-row">
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason (required to reject)" className="field flex-1" data-testid={`admin-kyc-note-${c.id}`} />
          <button disabled={busy} onClick={() => act("approve")} className="btn-primary !py-2" data-testid={`admin-kyc-approve-${c.id}`}><Check className="h-4 w-4" /> Approve</button>
          <button disabled={busy} onClick={() => act("reject")} className="inline-flex items-center justify-center gap-2 rounded-full border border-red-500/40 px-5 py-2 text-sm text-red-400 hover:bg-red-500/10 disabled:opacity-50" data-testid={`admin-kyc-reject-${c.id}`}><X className="h-4 w-4" /> Reject</button>
        </div>
      )}
    </div>
  );
}

export default function AdminKyc() {
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [preview, setPreview] = useState(null);
  const load = useCallback(() => api.get("/admin/kyc", { params: { status } }).then(({ data }) => setItems(data)).catch(() => {}), [status]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-kyc-page">
      <PageHead title="Identity verification" subtitle="Review client documents. Only approved clients can deposit, invest or withdraw." />
      <div className="mb-6 inline-flex rounded-full border border-line p-1">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setStatus(f)} data-testid={`admin-kyc-filter-${f}`}
            className={`rounded-full px-4 py-2.5 text-xs capitalize sm:py-1.5 transition-colors duration-200 ${status === f ? "bg-white/10 text-white" : "text-slate-400 hover:text-white"}`}>{f}</button>
        ))}
      </div>
      <div className="space-y-4">
        {items.map((c) => <KycCard key={c.id} c={c} onPreview={setPreview} onDone={load} />)}
        {!items.length && <p className="text-slate-500" data-testid="admin-kyc-empty">No {status === "all" ? "" : status} verifications.</p>}
      </div>
      <DocPreviewDialog doc={preview} onOpenChange={(o) => !o && setPreview(null)} />
    </div>
  );
}
